import React, { useEffect, useState } from "react";
import { ITicket } from "./HelpdeskQueryList";
import apiClient from "../../../api/axiosConfig";
import "./QueryResponseModal.css";

interface IAttachment {
  _id?: string;
  type?: string;
  data?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

interface IMessage {
  _id: string;
  senderId: string;
  messageText: string;
  sentOn: string;
  attachments?: IAttachment[];
}

interface Props {
  ticket: ITicket;
  onClose: () => void;
}

const ClosedQueryModal: React.FC<Props> = ({ ticket, onClose }) => {
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await apiClient.helpdeskApi.get(`/api/admin/tickets/${ticket._id}/messages`);
        let allMessages: IMessage[] = res.data || [];

        if (ticket.message) {
          allMessages = [
            {
              _id: "initial-msg",
              senderId: ticket.raisedBy,
              messageText: ticket.message,
              sentOn: ticket.createdAt,
              attachments: (ticket as any).attachments || ((ticket as any).attachment ? [(ticket as any).attachment] : undefined),
            },
            ...allMessages,
          ];
        }

        setMessages(allMessages);
      } catch (err) {
        console.error("Error fetching closed query messages:", err);
      }
    };
    fetchMessages();
  }, [ticket]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr || dateStr === "N/A") return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const toImageSrc = (data?: string, mimeType?: string) => {
    if (!data) return undefined;
    const trimmed = data.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith("data:image/") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    return `data:${mimeType || "image/jpeg"};base64,${trimmed}`;
  };

  const getMessageImage = (msg: IMessage) => {
    const attachmentCandidates = [
      ...(msg.attachments || []),
      ...((msg as any).attachment ? [(msg as any).attachment] : []),
      ...(Array.isArray((msg as any).media) ? (msg as any).media : (msg as any).media ? [(msg as any).media] : []),
      ...((msg as any).image ? [(msg as any).image] : []),
      ...((msg as any).file ? [(msg as any).file] : []),
    ];

    const attachment = attachmentCandidates.find((item: any) => {
      const type = item?.type || item?.mimeType || item?.kind || "";
      const data = item?.data || item?.base64 || item?.fileData || item?.image || item?.url;
      return type.includes("image") || !!data;
    });

    const imageData = attachment?.data || attachment?.base64 || attachment?.fileData || attachment?.image || attachment?.url;
    const mimeType = attachment?.mimeType || attachment?.type || "image/jpeg";

    if (imageData) return toImageSrc(imageData, mimeType);

    return undefined;
  };

  return (
    <div className="response-overlay">
      <div className="response-modal">
        <div className="response-header">
          <h3>Closed Query</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="response-body">
          <div className="response-left">
            <div className="ticket-details-box">
              <div className="ticket-details-header">
                <span className="ticket-id-title">{ticket._id}</span>
                <span className="status-pill closed">Closed</span>
              </div>
              <div className="ticket-details-grid">
                <div className="detail-item">
                  <span className="detail-label">Raised By</span>
                  <span className="detail-val strong">{ticket.parentName || "—"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Child Name</span>
                  <span className="detail-val strong">{ticket.childName || "—"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Issue Type</span>
                  <span className="detail-val strong">{ticket.issueType || "—"}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Created On</span>
                  <span className="detail-val strong">{formatDate(ticket.createdAt)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Screening Date</span>
                  <span className="detail-val strong">{formatDate(ticket.appointmentDate)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Ageing</span>
                  <span className="detail-val strong">{ticket.ageing ?? 0} days</span>
                </div>
              </div>
            </div>

            <div className="chat-area">
              {messages.map((msg) => {
                const imageSrc = getMessageImage(msg);
                const imagePattern = /^(📷\s*)?Image:\s*(.+)$/i;
                const isImageOnlyMsg = imagePattern.test(msg.messageText?.trim() || "");
                const imageFileName = msg.messageText?.trim().match(imagePattern)?.[2] || "";

                return (
                  <div
                    key={msg._id}
                    className={`chat-bubble-container ${
                      msg.senderId === "admin" ? "admin" : "user"
                    }`}
                  >
                    <div className="chat-bubble">
                      <div className="sender-info">
                        <strong>
                          {msg.senderId === "admin" ? "Admin" : ticket.parentName}
                        </strong>
                        <span>
                          {new Date(msg.sentOn).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>

                      {imageSrc && (
                        <img
                          className="chat-message-image"
                          src={imageSrc}
                          alt={imageFileName || "Chat attachment"}
                          onClick={() => setSelectedImage(imageSrc)}
                          style={{ cursor: "pointer" }}
                        />
                      )}

                      {!imageSrc && isImageOnlyMsg && (
                        <div className="chat-image-chip">
                          <span className="chat-image-chip-icon">🖼️</span>
                          <span className="chat-image-chip-name">{imageFileName}</span>
                        </div>
                      )}

                      {msg.messageText && !isImageOnlyMsg && (
                        <p className="message-text">{msg.messageText}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="closed-notice-banner">
              <span>🔒 This query is closed and read-only.</span>
            </div>
          </div>
          <div className="response-right">
            <h2>User Queries ({ticket.parentName || "User"})</h2>
            <div className="user-queries-table">
              <table>
                <thead>
                  <tr>
                    <th>Query ID</th>
                    <th>Issue Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="active-query-row">
                    <td>{ticket._id}</td>
                    <td>{ticket.issueType}</td>
                    <td><span className="status-pill closed">Closed</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <span>Showing 1 query</span>
            </div>
          </div>
        </div>
      </div>

      {selectedImage && (
        <div className="image-lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <div className="image-lightbox" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="image-lightbox-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close image"
            >
              ×
            </button>
            <img src={selectedImage} alt="Enlarged chat attachment" className="image-lightbox-img" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClosedQueryModal;


